import type {AhpSpaceSelection, PreparedPrompt} from '@github-ui/ahp-session-client/contracts'
import type {EstablishedAhpInitialSubmissionCoordinator} from '@github-ui/ahp-session-client/initial-submission'
import {IS_SERVER} from '@github-ui/ssr-utils'
import {useEffect, useRef, useState} from 'react'

import {clearComposerHandoff, readComposerHandoff, type ComposerHandoff} from './composer-handoff'
import {
  SUBMIT_ERROR_MESSAGE,
  type SubmissionFailure,
  userFacingSubmissionMessage,
} from '@github-ui/ahp-session-client/submission-error'

type EstablishedSession = Awaited<ReturnType<EstablishedAhpInitialSubmissionCoordinator['submit']>>

/**
 * Reads the turn handed off by an entry-point composer.
 *
 * Only the tab-scoped handoff store is consulted. The shared entry-point message
 * in `localStorage` is deliberately *not* a fallback: its producers target the
 * v1 immersive surface, so consuming it here would steal a prompt meant for
 * another page — and, being shared, one that may belong to a different tab.
 */
function readHandoff(): ComposerHandoff | null {
  return readComposerHandoff()
}

/**
 * What a failed replay cannot put back, phrased as the actions that restore it.
 *
 * Only the text survives. Attachments were prepared by a composer on the page
 * the turn came from and cannot be rebuilt into a draft here, and the space
 * travels as the `{owner, slug}` the submission contract carries rather than the
 * full picker selection, so neither can be reinstated for the resend. Saying so
 * is the difference between the user resending what they meant and resending a
 * turn quietly missing what they chose — the composer's own pickers would sit
 * empty either way.
 */
function unrestorable(handoff: ComposerHandoff): string[] {
  const actions: string[] = []
  const attachedRepository = handoff.metadata?.['repository'] !== undefined
  if ((handoff.prompt.attachments?.length ?? 0) > 0 || attachedRepository) actions.push('add your attachments')
  if (handoff.metadata?.['space'] !== undefined) actions.push('select your space')
  return actions
}

/** Names what the resend has to be given again, when anything was lost. */
export function replayFailureMessage(handoff: ComposerHandoff): string {
  const actions = unrestorable(handoff)
  if (actions.length === 0) return SUBMIT_ERROR_MESSAGE
  // An attachment-only turn is valid — the session input accepts an empty text
  // when attachments are present — so claiming its text was restored would
  // describe something the user never wrote.
  const restored = handoff.prompt.text.trim().length > 0 ? 'The text has been restored, but you' : 'You'
  return `Your message could not be sent. ${restored} will need to ${actions.join(' and ')} again.`
}

/**
 * Reduces a failed replay to what the composer should show and restore.
 *
 * The dispatch happens outside the composer's own submit path, so without this
 * the rejection has nowhere to surface: the user sees an empty conversation and
 * loses the turn they composed.
 */
function replayFailure(handoff: ComposerHandoff, error: unknown): SubmissionFailure {
  return {message: userFacingSubmissionMessage(error) ?? replayFailureMessage(handoff), text: handoff.prompt.text}
}

/**
 * Establishes the session for a turn composed on another page, exactly once.
 *
 * The turn was already prepared by the handing-off composer, so it is submitted
 * through the same initial-submission coordinator the receiving composer would
 * have used — carrying its attachments, model and space rather than a
 * reconstruction from its text.
 *
 * The handoff is claimed (cleared from storage) once, so no later visit can
 * replay it, and it is dispatched exactly once. React tears this effect down and
 * re-runs it — on every mount in development — and the runtime's own cleanup
 * resets the bootstrap as it goes, superseding an establish still in flight. The
 * turn survives that because the dispatch is deferred to a macrotask, so a
 * runtime torn down and rebuilt within the same commit is never handed a turn it
 * would immediately abandon. Attempts are also serialized, so an effect re-run
 * while one is in flight starts nothing.
 *
 * No failure is retried, and none is restored to storage: every rejection is
 * ambiguous — the task and turn may already have been accepted, and the
 * bootstrap's "superseded" error is no exception, since it is raised on both
 * sides of the underlying submit — so a resend could provision a duplicate. The
 * failure is reported to the caller instead, which surfaces it on the composer
 * along with the composed text, leaving a deliberate resend to the user.
 */
export function useComposerHandoffReplay({
  enabled,
  initialSubmission,
  onSessionEstablished,
}: {
  enabled: boolean
  initialSubmission: EstablishedAhpInitialSubmissionCoordinator | undefined
  onSessionEstablished?: (session: EstablishedSession) => void
}): {
  pending: boolean
  prompt: PreparedPrompt | undefined
  initialSelectedSpace: AhpSpaceSelection | undefined
  failure: SubmissionFailure | undefined
} {
  // The claimed turn, held in memory so a re-run can dispatch it again: it has
  // already been removed from storage, so losing it here would lose it entirely.
  const claimedRef = useRef<ComposerHandoff | null>(null)
  // Storage is read and cleared once, whatever happens afterwards. A second read
  // would find nothing, and re-reading is not how a re-dispatch gets its turn.
  const claimAttemptedRef = useRef(false)
  // Set once a live attempt has reported, so a later re-run does not resend a
  // turn that was accepted, nor silently retry one the user was told had failed.
  const settledRef = useRef(false)
  // Whether a dispatch is running. Attempts are deliberately serialized: a second
  // one started while the first is still in flight would provision a second task
  // and send the turn twice, and an effect can re-run for reasons that have
  // nothing to do with the runtime (an unrelated callback changing identity).
  const dispatchingRef = useRef(false)
  // Retries outlive the effect run that started them, so the coordinator and the
  // completion callback are read from refs rather than captured.
  const submissionRef = useRef(initialSubmission)
  const onEstablishedRef = useRef(onSessionEstablished)
  // Consulted before reporting: a late completion must not call back into a route
  // that has gone. `onSessionCreated` navigates to the new task URL, which would
  // pull the user into a chat they had already left.
  const mountedRef = useRef(true)
  // Synced after commit rather than during render, and read only from the
  // deferred dispatch, which runs after every effect for that commit has.
  useEffect(() => {
    submissionRef.current = initialSubmission
    onEstablishedRef.current = onSessionEstablished
  })
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])
  // The replay dispatches outside the composer's own submission lifecycle, so
  // nothing else marks the surface busy while the session provisions. Reported
  // back so the host can keep the composer non-composable: a turn submitted in
  // that window would be swallowed by the already-running bootstrap, and the
  // composer would clear the draft as though it had been sent.
  //
  // Read once during the first render rather than from the effect, so the window
  // never opens: a turn is already waiting before anything paints, and an enabled
  // composer on that first frame is exactly the race this closes. The parsed
  // record is kept so the effect does not parse the same payload a second time.
  //
  // Reading storage during render is only safe because this surface is never
  // server-rendered — the immersive controller sets `disable_ssr: true`, so the
  // initializer runs in the browser alone and there is no markup to mismatch.
  // `storage()` returns null without a `window`, so a server render seeds `null`,
  // which is the conservative value rather than a torn one.
  const [seededHandoff] = useState(() => (!IS_SERVER && enabled ? readHandoff() : null))
  const [pending, setPending] = useState(seededHandoff !== null)
  // Held in state rather than read from the claim ref during render, so the
  // surface re-renders with the turn as soon as it is claimed.
  const [replayPrompt, setReplayPrompt] = useState<PreparedPrompt | undefined>(() => seededHandoff?.prompt)
  const [initialSelectedSpace, setInitialSelectedSpace] = useState<AhpSpaceSelection | undefined>(
    () => seededHandoff?.initialSelectedSpace,
  )
  const [failure, setFailure] = useState<SubmissionFailure | undefined>(undefined)

  useEffect(() => {
    if (!enabled || !initialSubmission || settledRef.current || dispatchingRef.current) return

    // Claiming and dispatching are deferred by a tick rather than run inline, so a
    // runtime that is torn down and rebuilt within the same commit never receives
    // a turn it would immediately abandon. React does exactly that on every mount
    // in development (StrictMode mounts, cleans up, and mounts again), and the
    // runtime's cleanup resets the bootstrap as it goes — superseding an establish
    // dispatched inline and stranding the turn, having already provisioned a task
    // nothing will use.
    const timer = setTimeout(() => {
      if (!claimAttemptedRef.current) {
        claimAttemptedRef.current = true
        // Reuse the record read during the first render, so a large payload is
        // parsed once. Only re-read when there was no seed, which happens when
        // `enabled` or the coordinator arrived after mount.
        const stored = seededHandoff ?? readHandoff()
        // A turn that could not be removed is not claimed: replaying it would
        // leave it stored to be submitted again on the next visit. Settling
        // rather than submitting leaves the surface composable, not locked.
        if (stored && clearComposerHandoff()) {
          claimedRef.current = stored
          setReplayPrompt(stored.prompt)
          setInitialSelectedSpace(stored.initialSelectedSpace)
        }
      }

      const handoff = claimedRef.current
      if (!handoff) {
        setPending(false)
        return
      }

      dispatchingRef.current = true
      setPending(true)
      void (async () => {
        const submission = submissionRef.current
        if (!submission) {
          dispatchingRef.current = false
          setPending(false)
          return
        }
        let established: EstablishedSession | undefined
        try {
          established = await submission.submit({
            prompt: handoff.prompt,
            origin: 'new',
            ...(handoff.metadata ? {metadata: handoff.metadata} : {}),
            ...(handoff.initialSelectedSpace ? {initialSelectedSpace: handoff.initialSelectedSpace} : {}),
          })
          settledRef.current = true
        } catch (error) {
          settledRef.current = true
          // Never retried, including the bootstrap's "superseded" error. That one
          // reads like proof the turn was abandoned before it left, but the
          // bootstrap raises it identically on both sides of
          // `input.submit` — pre-dispatch, and again while awaiting acceptance —
          // and every variant carries `operation: 'establish'`, so a caller
          // cannot tell them apart. Re-sending on the ambiguous ones would
          // provision a second task and deliver the turn twice, which is why the
          // coordinator itself never retries.
          //
          // Nor is the turn restored to storage: the next visit would resend it
          // for the same reason. Nothing else reports this — the dispatch
          // bypasses the composer's own submit path — so without reporting it
          // here the user is left on an empty conversation with the turn gone.
          // The composer surfaces the failure and restores the text, leaving a
          // deliberate resend to the user.
          // Nothing to tell an unmounted surface, and setting state on it is
          // pointless work.
          if (mountedRef.current) setFailure(replayFailure(handoff, error))
        } finally {
          dispatchingRef.current = false
          if (mountedRef.current) setPending(false)
        }

        // Deliberately outside the block above: by this point the turn has been
        // accepted and the session exists with the turn in it. Were reporting
        // to throw and be caught as a submission failure, the composer would
        // restore the draft and invite a resend, delivering the turn twice into
        // that very session. Nothing here can undo an accepted turn, so there is
        // nothing to report to the user; a throw escapes as an unhandled
        // rejection and reaches error reporting on its own.
        //
        // Not reported at all once the surface is gone: reporting navigates to
        // the task URL and would drag the user back into a chat they have left.
        // The session is unaffected and reachable from the sidebar.
        if (established && mountedRef.current) {
          onEstablishedRef.current?.(established)
        }
      })()
    }, 0)

    return () => {
      clearTimeout(timer)
    }
  }, [enabled, initialSubmission, onSessionEstablished, seededHandoff])

  // The turn being replayed, so the surface can show it while it provisions
  // rather than a generic "opening" state: this is the user's own first turn, and
  // it should look like one from the moment they arrive.
  return {pending, prompt: pending ? replayPrompt : undefined, initialSelectedSpace, failure}
}
